import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import styled from "styled-components";
import { Bar, Line } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
	Chart as ChartJS,
	ArcElement,
	BarElement,
	CategoryScale,
	Filler,
	Legend,
	LineElement,
	PointElement,
	LinearScale,
	SubTitle,
	Title,
	Tooltip,
} from "chart.js";
import { Row, Col, Container, Spinner } from "react-bootstrap";
import API from "../../network/dashboardAPI";
import moment from "moment";
import {
	Paper,
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	TablePagination,
	MenuItem,
} from "@material-ui/core";
import {
	Label,
	ListAlt,
	PermContactCalendar,
	ShoppingBasket,
	ShoppingCart,
} from "@material-ui/icons";
import axios from "axios";
import { baseUrl } from "../../constants/Constant";
import download from "js-file-download";

ChartJS.register(
	ArcElement,
	BarElement,
	CategoryScale,
	ChartDataLabels,
	Filler,
	Legend,
	LineElement,
	PointElement,
	LinearScale,
	SubTitle,
	Title,
	Tooltip
);

const labels = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

const color = {
	cyan: "rgba(75, 192, 192, 1)",
	blue: "rgba(53, 162, 235, 1)",
	yellow: "rgba(255, 206, 86, 1)",
	purple: "rgba(156, 136, 255, 1)",
};

// Intl.NumberFormat is costly to construct and the charts format every label on
// each animation frame, so the formatters are built once.
const numberFormat = new Intl.NumberFormat(undefined, {});
const currencyFormat = new Intl.NumberFormat(undefined, {
	style: "currency",
	currency: "USD",
});
const currencyCompactFormat = new Intl.NumberFormat(undefined, {
	style: "currency",
	currency: "USD",
	notation: "compact",
});

const getCurrencyFormatDisplay = (value, notation = "standard") => {
	if (value === null || value === undefined) return null;
	const formatter = notation === "compact" ? currencyCompactFormat : currencyFormat;
	return formatter.format(value);
};

// react-chartjs-2 runs chart.update() on every render of the chart element, which
// replays the animation; memoising limits that to real data changes.
const MemoBar = React.memo(Bar);
const MemoLine = React.memo(Line);

const currentYear = moment().format("YYYY");

const yearOptions = Array.from({ length: 6 }, (_, i) =>
	String(Number(currentYear) - i)
);

const YearSelect = ({ value, onChange }) => (
	<TextField
		select
		variant="outlined"
		size="small"
		value={value}
		onChange={(e) => onChange(e.target.value)}
		style={{ background: "#fff", minWidth: 100 }}
	>
		{yearOptions.map((year) => (
			<MenuItem key={year} value={year}>
				{year}
			</MenuItem>
		))}
	</TextField>
);

export const Analytics = (props) => {
	const [orderData, setOrderData] = useState([]);
	const [revenueData, setRevenueData] = useState([]);
	const [customerData, setCustomerData] = useState({});
	const [overviewData, setOverviewData] = useState({});
	const [topSellingProducts, setTopSellingProducts] = useState({});
	const [overviewLoading, setOverviewLoading] = useState(false);
	const [loading, setLoading] = useState(false);

	const [orderYear, setOrderYear] = useState(currentYear);
	const [revenueYear, setRevenueYear] = useState(currentYear);
	const [customerYear, setCustomerYear] = useState(currentYear);

	const [dialogImgOpen, setDialogImgOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState({});

	// pagination
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");

	const isValidDate = (date) => date && moment(date).isValid();

	const isErrorDate = useMemo(() => {
		return moment(startDate).isAfter(moment(endDate));
	}, [startDate, endDate]);

	const filterQuery = useMemo(() => {
		return {
			year: currentYear,
			startDate: isValidDate(startDate)
				? moment(startDate).format("YYYY-MM-DD HH:mm:ss")
				: "",
			endDate: isValidDate(endDate)
				? moment(endDate).format("YYYY-MM-DD HH:mm:ss")
				: "",
			limit: rowsPerPage,
			page: page + 1
		};
	}, [endDate, startDate, rowsPerPage, page]);

	const ordersAnimated = useRef(false);
	const revenueAnimated = useRef(false);

	const handleOpenDialogImgOpen = (item = {}) => {
		return () => {
			setDialogImgOpen((prev) => !prev);
			setSelectedItem(item);
		};
	};

	const getOverviewsRecords = (query) => {
		setOverviewLoading(true);
		API.getOverviewsAnalytic(query)
			.then((response) => response.json())
			.then((response) => {
				if (response.success) {
					setOverviewData(response.data);
				} else {
					console.log(response.message);
				}
				setOverviewLoading(false);
			})
			.catch((error) => {
				console.log("Debug error: ", error);
				setOverviewLoading(false);
			});
	};

	const getOrdersAnalytic = (query) => {
		API.getOrdersAnalytic(query)
			.then((response) => response.json())
			.then((response) => {
				if (response.success) {
					ordersAnimated.current = false;
					setOrderData(response.data);
				} else {
					console.log(response.message);
				}
			})
			.catch((error) => console.log("Debug error: ", error));
	};

	const getRevenueAnalytic = (query) => {
		API.getRevenueAnalytic(query)
			.then((response) => response.json())
			.then((response) => {
				if (response.success) {
					revenueAnimated.current = false;
					setRevenueData(response.data);
				} else {
					console.log(response.message);
				}
			})
			.catch((error) => console.log("Debug error: ", error));
	};

	const getCustomerRecords = (query) => {
		API.getCustomersAnalytic(query)
			.then((response) => response.json())
			.then((response) => {
				if (response.success) {
					setCustomerData(response.data);
				} else {
					console.log(response.message);
				}
			})
			.catch((error) => console.log("Debug error: ", error));
	};

	const getTopSellingProducts = (query) => {
		setLoading(true);
		API.getTopSellingProductsAnalytic(query)
			.then((response) => response.json())
			.then((response) => {
				if (response.success) {
					setTopSellingProducts(response.data);
				} else {
					console.log(response.message);
				}
				setLoading(false);
			})
			.catch((error) => {
				console.log("Debug error: ", error);
				setLoading(false);
			});
	};

	const renderOverviewItemData = (type) => {
		let data = 0;
		switch (type) {
			case "orders":
				data = overviewData?.totalOrders;
				break;
			case "customers":
				data = overviewData?.totalCustomers;
				break;
			case "products":
				data = overviewData?.totalProducts;
				break;
			case "brands":
				data = overviewData?.totalBrands;
				break;
			default:
				break;
		}

		return overviewLoading ? (
			<Spinner animation="border" variant="secondary" size="sm" />
		) : (
			<p className="d-block h4 m-0 p-0 ml-auto">{numberFormat.format(data || 0)}</p>
		);
	};

	const handelExtract = async () => {
		let queryString = Object.keys(filterQuery).reduce((acc, key) => {
			return (acc += `${key}=${filterQuery[key]}&`);
		}, "");
		try {
			const response = await axios({
				method: "GET",
				url: `${baseUrl}api/v1/admin/analytic/best-sellers/extract?${queryString}`,
				headers: {
					Authorization: localStorage.getItem("token"),
					"Content-Type": "application/xlsx",
				},
				responseType: "blob",
			});
			const formatStartDate = isValidDate(startDate)
				? moment(startDate).format("YYYY-MM-DD")
				: moment().startOf("month").format("YYYY-MM-DD");

			const formatEndDate = isValidDate(endDate)
				? moment(endDate).format("YYYY-MM-DD")
				: moment().endOf("month").format("YYYY-MM-DD");

			if (response.status === 200) {
				download(
					response.data,
					`BEST_SELLING_PRODUCTS_${formatStartDate}-${formatEndDate}.xlsx`
				);
			}
		} catch (error) {
			alert("Download failed!");
			console.log("Debug error: ", error);
		}
	};

	const handleChangeDate = (e) => {
		let { name, value } = e.target;
		switch (name) {
			case "startDate":
				setStartDate(value);
				if (value === "" && endDate) {
					setEndDate("");
				}
				break;
			case "endDate":
				setEndDate(value);
				if (value === "" && startDate) {
					setStartDate("");
				}
				break;
			default:
				break;
		}
	};
	
	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};
	
	const handleChangeRowsPerPage = (event) => {
		setPage(0);
		setRowsPerPage(parseInt(event.target.value));
	};
	

	useEffect(() => {
		// hold off while the range is half-entered or reversed
		if (isErrorDate || (startDate && !endDate)) return;
		getTopSellingProducts(filterQuery);
	}, [filterQuery, isErrorDate, startDate, endDate]);

	useEffect(() => {
		getOverviewsRecords({ year: currentYear });
	}, []);

	useEffect(() => {
		getOrdersAnalytic({ year: orderYear });
	}, [orderYear]);

	useEffect(() => {
		getRevenueAnalytic({ year: revenueYear });
	}, [revenueYear]);

	useEffect(() => {
		getCustomerRecords({ year: customerYear });
	}, [customerYear]);

	const ordersChartOptions = useMemo(
		() => ({
			responsive: true,
			animation: {
				onComplete: () => {
					ordersAnimated.current = true;
				},
				delay: (context) => {
					let delay = 0;
					if (
						context.type === "data" &&
						context.mode === "default" &&
						!ordersAnimated.current
					) {
						delay = context.dataIndex * 300 + context.datasetIndex * 100;
					}
					return delay;
				},
			},
			scales: {
				y: {
					ticks: {
						font: {
							size: 14,
						},
					},
					stacked: true,
				},
				x: {
					ticks: {
						font: {
							size: 14,
							weight: 600,
						},
					},
					stacked: true,
				},
			},
			plugins: {
				datalabels: {
					display: true,
					anchor: "end",
					align: "top",
					font: {
						size: 14,
					},
				},
				title: {
					display: true,
					text: "Orders",
					align: "start",
					color: "#000",
					font: {
						size: 23,
					},
				},
				subtitle: {
					display: true,
					text: `Total orders (success & abandon) in ${orderYear}`,
					position: "top",
					align: "start",
					font: {
						size: 16,
					},
					padding: { bottom: 20 },
				},
				tooltip: {
					enabled: true,
					titleFont: { size: 16 },
					bodyFont: { size: 16 },
					boxPadding: 6,
					padding: 12,
				},
				legend: {
					position: "bottom",
					align: "start",
					labels: {
						font: {
							size: 16,
						},
					},
				},
			},
		}),
		[orderYear]
	);

	const ordersChartData = useMemo(
		() => ({
			labels,
			datasets: [
				{
					label: "Express",
					data: orderData?.data?.map((item) => item.expressSuccess),
					backgroundColor: "#f01266",
					stack: 1,
					datalabels: {
						color: "#f01266",
					},
				},
				{
					label: "Normal",
					data: orderData?.data?.map((item) => item.normalSuccess),
					backgroundColor: "#9cc54b",
					stack: 1,
					datalabels: {
						color: "#9cc54b",
					},
				},
				{
					label: "Abandon (Express)",
					data: orderData?.data?.map((item) => item.expressAbandon),
					backgroundColor: "#8b939a",
					stack: 2,
					datalabels: {
						color: "#000",
					},
				},
				{
					label: "Abandon (Normal)",
					data: orderData?.data?.map((item) => item.normalAbandon),
					backgroundColor: "#a0a7ad",
					stack: 2,
					datalabels: {
						color: "#000",
					},
				},
			],
		}),
		[orderData]
	);

	const revenueChartOptions = useMemo(
		() => ({
			responsive: true,
			animation: {
				onComplete: () => {
					revenueAnimated.current = true;
				},
				delay: (context) => {
					let delay = 0;
					if (
						context.type === "data" &&
						context.mode === "default" &&
						!revenueAnimated.current
					) {
						delay = context.dataIndex * 300 + context.datasetIndex * 100;
					}
					return delay;
				},
			},
			scales: {
				y: {
					ticks: {
						color: "#28a745",
						font: {
							size: 14,
						},
						callback: function (value) {
							return getCurrencyFormatDisplay(value, "compact");
						},
					},
				},
				x: {
					ticks: {
						font: {
							size: 14,
							weight: 600,
						},
					},
				},
			},
			plugins: {
				datalabels: {
					display: true,
					anchor: "end",
					align: "top",
					color: "#28a745",
					font: {
						size: 14,
					},
					formatter: function (value, content) {
						return getCurrencyFormatDisplay(value);
					},
				},
				title: {
					display: true,
					text: "Revenue",
					align: "start",
					color: "#000",
					font: {
						size: 23,
					},
				},
				subtitle: {
					display: true,
					text: `Annual revenue in ${revenueYear}`,
					position: "top",
					align: "start",
					font: {
						size: 16,
					},
					padding: { bottom: 20 },
				},
				tooltip: {
					enabled: true,
					titleFont: { size: 16 },
					bodyFont: { size: 16 },
					boxPadding: 6,
					padding: 12,
					callbacks: {
						label: function (item) {
							return getCurrencyFormatDisplay(item.raw);
						},
					},
				},
				legend: {
					display: false,
				},
			},
		}),
		[revenueYear]
	);

	const revenueChartData = useMemo(
		() => ({
			labels,
			datasets: [
				{
					label: "Avenue",
					data: revenueData?.data?.map((item) => item.revenue),
					backgroundColor: "#9cc54b",
				},
			],
		}),
		[revenueData]
	);

	const customerChartOptions = useMemo(
		() => ({
			responsive: true,
			scales: {
				y: {
					ticks: {
						font: {
							size: 14,
						},
					},
				},
				x: {
					ticks: {
						font: {
							size: 14,
							weight: 600,
						},
					},
				},
			},
			plugins: {
				datalabels: {
					display: true,
					anchor: "end",
					align: "top",
					color: "rgba(53, 162, 235, 1)",
					font: {
						size: 14,
					},
				},
				title: {
					display: true,
					fullSize: true,
					text: "New Customer Acquisition",
					align: "start",
					color: "#000",
					font: {
						size: 23,
					},
				},
				subtitle: {
					display: true,
					fullSize: true,
					text: `Total: ${customerData?.total ?? 0} (${customerYear})`,
					font: {
						size: 16,
					},
					padding: { bottom: 20 },
					align: "start",
				},
				tooltip: {
					enabled: true,
					titleFont: { size: 16 },
					bodyFont: { size: 16 },
					boxPadding: 6,
					padding: 12,
				},
				legend: {
					display: false,
				},
			},
		}),
		[customerData, customerYear]
	);

	const customerChartData = useMemo(
		() => ({
			labels: labels,
			datasets: [
				{
					label: "Customers",
					data: customerData?.data?.map((item) => item?.count),
					fill: true,
					backgroundColor: "rgba(53, 162, 235, 0.25)",
					borderColor: "rgba(53, 162, 235, 0.75)",
					pointBackgroundColor: "rgba(53, 162, 235, 1)",
					borderJoinStyle: "round",
					pointBorderWidth: 3,
					pointHoverBorderWidth: 6,
					tension: 0.3,
				},
			],
		}),
		[customerData]
	);

	return (
		<HelpPageWrapper>
			<Container
				fluid
				className="d-flex flex-column m-0 p-0"
				style={{ gap: "1rem" }}
			>
				<Row>
					<Col xs={12}>
						<div
							className="d-flex align-items-center"
							style={{ paddingBottom: "0.25rem", minHeight: 40 }}
						>
							<p
								style={{
									color: "#000",
									fontSize: 23,
									fontWeight: 700,
								}}
							>
								Overviews
							</p>
						</div>
						<OverviewWrapper>
							<OverviewItem className="rounded shadow-sm">
								<div className="d-flex align-items-end">
									<ShoppingCart fontSize="large" style={{ color: color.cyan }} />
									<p className="title text-muted ml-3" style={{ fontWeight: 500 }}>
										Total Orders
									</p>
								</div>
								<div className="d-flex justify-content-between align-items-end">
									<Link to="/admin/orders" className="text-muted">
										View
									</Link>
									{renderOverviewItemData("orders")}
								</div>
							</OverviewItem>
							<OverviewItem className="rounded shadow-sm">
								<div className="d-flex align-items-end">
									<PermContactCalendar fontSize="large" style={{ color: color.blue }} />
									<p className="title text-muted ml-2" style={{ fontWeight: 500 }}>
										Total Customers
									</p>
								</div>
								<div className="d-flex justify-content-between align-items-end">
									<Link to="/admin/customers" className="text-muted">
										View
									</Link>
									{renderOverviewItemData("customers")}
								</div>
							</OverviewItem>
							<OverviewItem className="rounded shadow-sm">
								<div className="d-flex align-items-end">
									<ShoppingBasket fontSize="large" style={{ color: color.yellow }} />
									<p className="title text-muted ml-2" style={{ fontWeight: 500 }}>
										Total Products
									</p>
								</div>
								<div className="d-flex justify-content-between align-items-end">
									<Link to="/admin/products" className="text-muted">
										View
									</Link>
									{renderOverviewItemData("products")}
								</div>
							</OverviewItem>
							<OverviewItem className="rounded shadow-sm">
								<div className="d-flex align-items-end">
									<Label fontSize="large" style={{ color: color.purple }} />
									<p className="title text-muted ml-2" style={{ fontWeight: 500 }}>
										Total Brands
									</p>
								</div>
								<div className="d-flex justify-content-between align-items-end">
									<Link to="/admin/brands" className="text-muted">
										View
									</Link>
									{renderOverviewItemData("brands")}
								</div>
							</OverviewItem>
						</OverviewWrapper>
					</Col>
				</Row>
				<Row>
					<Col xs={12}>
						<ChartCard className="bg-white p-3 rounded shadow-sm">
							<ChartCardHeader>
								<YearSelect value={orderYear} onChange={setOrderYear} />
							</ChartCardHeader>
							<MemoBar options={ordersChartOptions} data={ordersChartData} />
						</ChartCard>
					</Col>
				</Row>
				<Row>
					<Col xs={12}>
						<ChartCard className="bg-white p-3 rounded shadow-sm">
							<ChartCardHeader>
								<YearSelect value={revenueYear} onChange={setRevenueYear} />
							</ChartCardHeader>
							<MemoBar options={revenueChartOptions} data={revenueChartData} />
						</ChartCard>
					</Col>
				</Row>
				<Row>
					<Col xs={12}>
						<ChartCard className="bg-white p-3 rounded shadow-sm">
							<ChartCardHeader>
								<YearSelect value={customerYear} onChange={setCustomerYear} />
							</ChartCardHeader>
							<MemoLine options={customerChartOptions} data={customerChartData} />
						</ChartCard>
					</Col>
				</Row>
				<Row>
					<Col xs={12}>
						<TopSellingProductsWrapper>
							<Paper className="bg-white p-3 rounded shadow-sm">
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										gap: 10,
										paddingBottom: 15,
									}}
								>
									<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
										<p
											style={{
												color: "#000",
												fontSize: 23,
												fontWeight: 700,
											}}
										>
											Top Selling Products
										</p>
										{(!startDate || !endDate) && <p>({moment().format("MMMM")})</p>}
									</div>
									<div
										style={{
											display: "flex",
											gap: 10,
											alignItems: "center",
											flexWrap: "wrap",
										}}
									>
										<TextField
											type="date"
											variant="outlined"
											size="small"
											name="startDate"
											value={startDate}
											error={isErrorDate}
											helperText={
												isErrorDate && (
													<span className="text-danger">
														Start date must be after end date
													</span>
												)
											}
											onChange={handleChangeDate}
										/>
										<TextField
											type="date"
											variant="outlined"
											size="small"
											name="endDate"
											value={endDate}
											disabled={!startDate}
											onChange={handleChangeDate}
										/>
										<Button
											variant="contained"
											color="primary"
											disabled={!topSellingProducts?.data?.length}
											startIcon={<ListAlt />}
											onClick={handelExtract}
										>
											Extract
										</Button>
									</div>
								</div>
								<TableContainer>
									<Table stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell component="th">SKU</TableCell>
												<TableCell component="th">Image</TableCell>
												<TableCell component="th">Name</TableCell>
												<TableCell component="th" align="center">
													Cost Price
												</TableCell>
												<TableCell component="th" align="center">
													Quantity
												</TableCell>
												<TableCell component="th">Category</TableCell>
												<TableCell component="th">Supplier Name</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{loading ? (
												<TableRow>
													<TableCell colSpan={7}>
														<Spinner size="sm" animation="border" /> Loading...
													</TableCell>
												</TableRow>
											) : topSellingProducts?.data?.length ? (
												topSellingProducts?.data?.map((item) => {
													return (
														<TableRow key={item?.item_id} hover>
															<TableCell>{item?.sku}</TableCell>
															<TableCell>
																<img
																	className="product-img"
																	src={item?.image}
																	alt={item?.name}
																	onClick={handleOpenDialogImgOpen(item)}
																/>
															</TableCell>
															<TableCell>{item?.name}</TableCell>
															<TableCell align="center">
																{item.cost
																? "$" + parseFloat(item.cost).toFixed(2)
																: "N/A"}
															</TableCell>
															<TableCell align="center">{item?.total_sales}</TableCell>
															<TableCell>{item?.cat_name}</TableCell>
															<TableCell>{item?.supplier}</TableCell>
														</TableRow>
													);
												})
											) : (
												<TableRow>
													<TableCell colSpan={7} align="center">
														No Data
													</TableCell>
												</TableRow>
											)}
											<TableRow></TableRow>
										</TableBody>
									</Table>
								</TableContainer>
								<TablePagination
									component="div"
									count={topSellingProducts?.total_items || 0}
									page={page}
									rowsPerPage={rowsPerPage}
									rowsPerPageOptions={[5, 10, 25, 50]}
									onPageChange={handleChangePage}
									onRowsPerPageChange={handleChangeRowsPerPage}
								/>
							</Paper>
						</TopSellingProductsWrapper>
					</Col>
				</Row>
			</Container>

			<Dialog
				open={dialogImgOpen}
				maxWidth="xs"
				onClose={handleOpenDialogImgOpen({})}
			>
				<DialogTitle>{selectedItem?.name}</DialogTitle>
				<DialogContent dividers>
					<div className="d-flex justify-content-center align-items-center">
						<img
							style={{
								display: "inline",
								width: 300,
								height: "auto",
								objectFit: "contain",
								aspectRatio: "1/1",
							}}
							src={selectedItem?.image}
							alt={selectedItem?.name}
						/>
					</div>
				</DialogContent>
				<DialogActions>
					<Button
						size="small"
						variant="contained"
						onClick={handleOpenDialogImgOpen({})}
					>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</HelpPageWrapper>
	);
};

// style
const HelpPageWrapper = styled.div`
	background-color: #f7f7f7;
	position: relative;
	width: 100%;
	padding: 15px 0;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;

	@media (max-width: 989px) {
		padding-top: 70px;
	}
	& > div.row {
		padding: 0;
		margin: 0;
	}
`;

const ChartCard = styled.div`
	position: relative;
`;

const ChartCardHeader = styled.div`
	position: absolute;
	top: 0.75rem;
	right: 0.75rem;
	z-index: 1;
`;

const OverviewWrapper = styled.div`
	width: 100%;
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
	gap: 1rem;
`;

const OverviewItem = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	padding: 0.75rem;
	border-radius: 0.25rem;
	background: #fff;
	cursor: pointer;
	transition: transform 0.2s ease-in-out;
	transform-origin: center;

	.title {
		font-size: 1.075rem;
	}

	&:hover {
		transform: scale(1.035);
	}
`;

const TopSellingProductsWrapper = styled.div`
	table {
		thead tr th {
			width: initial;
			vertical-align: middle;
			font-weight: 600;
			font-size: 0.95rem;
			background: #efefef;
		}

		tbody tr td {
			vertical-align: middle;
			white-space: nowrap;
			font-size: 0.95rem;
			padding: 8px 16px;

			.product-img {
				display: block;
				object-fit: contain;
				width: 50px;
				height: 50px;
				cursor: pointer;
			}
		}
	}

	.MuiDialogContent-dividers {
		padding: 16px;
	}

	.MuiTableContainer-root {
		::-webkit-scrollbar {
			width: 6px;
			height: 6px;
			border-radius: 3px;
		}

		::-webkit-scrollbar-thumb {
			background: #888;
			border-radius: 6px;

			&:hover {
				background: #666;
			}
			&:active {
				background: #555;
			}
		}
	}
`;

const mapStateToProps = (state) => ({});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(Analytics);
